// Sijoitustaulun puhdas logiikka: tekstit, sijojen merkintä ja ero kärkeen (näkymä: components/LeaderboardPage.jsx).
// Palvelin (backend/app/leaderboard.py) laskee luvut ja sijat; tässä ne vain muotoillaan luettaviksi.

export const PERIODS = [
  { id: "all", label: "Kaikki" },
  { id: "week", label: "Viikko" },
  { id: "month", label: "Kuukausi" },
];

const number = (n) => n.toLocaleString("fi-FI");

/** Suomen monikko: 1 ruutu, 2 ruutua. */
export const plural = (n, one, many) => `${number(n)} ${n === 1 ? one : many}`;

/**
 * Pelaajan lyhyt yhteenveto riville: ruudut, maksimiklusteri (suurin yhtenäinen ruutujoukko, kun on yksikin),
 * kohteet, reitit ja retket. Salaiset löydöt ja kilometrit
 * näytetään vasta, kun niitä on, jotta rivi pysyy lyhyenä eikä paljasta salaisten kohteiden olemassaoloa turhaan.
 */
export function statParts(row) {
  const parts = [plural(row.tiles, "ruutu", "ruutua")];
  if (row.cluster > 0) parts.push(`klusteri ${number(row.cluster)}`);
  parts.push(
    plural(row.pois, "kohde", "kohdetta"),
    plural(row.routes, "reitti", "reittiä"),
  );
  if (row.secrets > 0) parts.push(plural(row.secrets, "salainen", "salaista"));
  parts.push(plural(row.trips, "retki", "retkeä"));
  if (row.km > 0) parts.push(`${row.km.toLocaleString("fi-FI", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`);
  return parts;
}

/** Mitalin väri kolmelle parhaalle, kun pisteitä on. Tasatilanteessa kaikki samalla sijalla saavat saman värin. */
export function medal(row) {
  if (!row || row.points <= 0) return "";
  return { 1: "gold", 2: "silver", 3: "bronze" }[row.rank] ?? "";
}

/** Oma tilanne yhdellä rivillä: "Johdat 120 pisteellä", "85 pistettä kärjestä" tai tasatilanne. */
export function gapLine(rows, myId) {
  const me = rows?.find((r) => r.id === myId);
  if (!me || rows.length < 2) return "";
  const leader = rows[0];
  if (rows.every((r) => r.points === 0)) return "Kaikki lähtevät nollasta. Ensimmäiset pisteet ratkaisevat.";
  if (me.rank === 1) {
    const tied = rows.filter((r) => r.id !== me.id && r.rank === 1);
    if (tied.length) return "Olet tasoissa kärjessä.";
    const next = rows.find((r) => r.id !== me.id);
    return `Johdat ${number(me.points - next.points)} pisteellä.`;
  }
  return `${plural(leader.points - me.points, "piste", "pistettä")} johtajasta (${leader.name}).`;
}

const shortDay = (unixSeconds) => {
  const d = new Date(unixSeconds * 1000);
  return `${d.getDate()}.${d.getMonth() + 1}.`;
};

/** Kauden selite otsikon alle: "5.10.–11.10." tai "Koko peli". */
export function periodHint(board) {
  if (!board || board.period === "all" || board.from == null || board.until == null) return "Koko peli";
  // until on seuraavan kauden ensimmäinen hetki, joten viimeinen päivä on sitä edeltävä.
  return `${shortDay(board.from)}–${shortDay(board.until - 3600)}`;
}

// --- Edellisen kauden voittajat ------------------------------------------------------------------

// Kuukauden nimi genetiivissä ("syyskuun voittaja"). Kuukausi luetaan Suomen ajassa, koska kausi alkaa Suomen keskiyöstä.
const MONTHS_GENITIVE = ["tammikuun", "helmikuun", "maaliskuun", "huhtikuun", "toukokuun", "kesäkuun", "heinäkuun", "elokuun", "syyskuun", "lokakuun", "marraskuun", "joulukuun"];
const monthFormat = new Intl.DateTimeFormat("fi-FI", { month: "numeric", timeZone: "Europe/Helsinki" });

/** Kauden nimi voittajalauseeseen: viikko -> "viime viikon", kuukausi -> "syyskuun". */
export function lastPeriodName(period, fromSeconds) {
  if (period === "week") return "viime viikon";
  const month = Number(monthFormat.format(new Date(fromSeconds * 1000)));
  return MONTHS_GENITIVE[month - 1] ?? "viime kuun";
}

const joinNames = (names) => (names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} ja ${names[names.length - 1]}`);

/**
 * Voittajarivi kortille ja ilmoitukseen. Palauttaa null, kun kautta ei kisattu (ei voittajia).
 *   { title: "Viime viikon voittaja", text: "Aino, 310 p", mine: false, names: ["Aino"] }
 * Omasta voitosta kerrotaan suoraan ("Voitit viime viikon!").
 */
export function winnerInfo(period, data, myId) {
  const winners = data?.winners ?? [];
  if (!winners.length) return null;
  const name = lastPeriodName(period, data.from);
  const mine = winners.some((w) => w.id === myId);
  const names = winners.map((w) => w.name);
  const points = `${number(winners[0].points)} p`;
  const title = `${name[0].toUpperCase()}${name.slice(1)} ${winners.length > 1 ? "voittajat" : "voittaja"}`;
  const text = mine
    ? (winners.length > 1 ? `Voititte tasapisteillä: ${joinNames(names)}, ${points}` : `Voitit! ${points}`)
    : `${joinNames(names)}, ${points}`;
  return { title, text, mine, names, period };
}

/**
 * Ilmoitukset, joita pelaaja ei ole vielä nähnyt. `seen` on { [pelaaja]: { week: alkuhetki, month: alkuhetki } };
 * ilmoitus näytetään kerran kutakin kautta kohden. Kuukausi ennen viikkoa, ettei tuoreempi peitä sitä.
 */
export function pendingWinnerNotices(winners, seen, myId) {
  const mySeen = seen?.[myId] ?? {};
  const out = [];
  for (const period of ["month", "week"]) {
    const data = winners?.[period];
    const info = winnerInfo(period, data, myId);
    if (info && mySeen[period] !== data.from) out.push({ ...info, from: data.from });
  }
  return out;
}

/** Merkitsee ilmoitukset nähdyiksi palauttamatta alkuperäistä oliota muuttuneena. */
export function markWinnersSeen(seen, myId, notices) {
  const mine = { ...(seen?.[myId] ?? {}) };
  for (const n of notices) mine[n.period] = n.from;
  return { ...(seen ?? {}), [myId]: mine };
}

// --- Joukkueiden ja perheiden vertailu -------------------------------------------------------------

/** Rivin tyyppisana: "Perhe" tai "Joukkue" (nimi näytetään erikseen). */
export const teamKindLabel = (row) => (row?.kind === "family" ? "Perhe" : "Joukkue");

/** Joukkuerivin lyhyt yhteenveto: jäsenmäärä ja ruudut. Arkistoitu merkitään, jotta tyhjä rivi ei hämmennä. */
export function teamStatParts(row) {
  const parts = [plural(row.members, "jäsen", "jäsentä"), plural(row.tiles, "ruutu", "ruutua")];
  if (row.status === "archived") parts.push("arkistoitu");
  return parts;
}

/**
 * Oman joukkueen tilanne yhdellä rivillä, samaan tapaan kuin pelaajien gapLine.
 * Tyhjä merkkijono, kun käyttäjällä ei ole joukkuetta tai vertailtavia on vain yksi.
 */
export function teamGapLine(rows, myTeamId) {
  const me = rows?.find((r) => r.id === myTeamId);
  if (!me || rows.length < 2) return "";
  if (rows.every((r) => r.points === 0)) return "Kaikki lähtevät nollasta. Ensimmäiset pisteet ratkaisevat.";
  if (me.rank === 1) {
    if (rows.some((r) => r.id !== me.id && r.rank === 1)) return "Olette tasoissa kärjessä.";
    const next = rows.find((r) => r.id !== me.id);
    return `Johdatte ${number(me.points - next.points)} pisteellä.`;
  }
  const leader = rows[0];
  return `${plural(leader.points - me.points, "piste", "pistettä")} johtajasta (${leader.name}).`;
}

/**
 * Jakaa taulun palkintokorokkeeseen (kolme ensimmäistä riviä, joilla on pisteitä) ja loppulistaan.
 * Korokkeelle ei nosteta nollapisteisiä: "kolmas sija" ilman pisteitä olisi harhaanjohtava.
 */
export function splitPodium(rows) {
  const list = rows ?? [];
  const podium = [];
  for (const row of list) {
    if (podium.length === 3 || row.points <= 0) break;
    podium.push(row);
  }
  return { podium, rest: list.slice(podium.length) };
}
